<script setup>
import { ref, reactive, computed, watch, useId } from 'vue';
import RadioGroup from '@/components/RadioGroup.vue';
import RadioRich from '@/components/RadioRich.vue';
import { useSpaBreakRooms } from '../composables/useSpaBreakRooms.js';

const props = defineProps({
    apiUrl:     { type: String, required: true },
    spaBreakId: { type: Number, required: true },
    locationId: { type: Number, required: true },
    date:       { type: String, required: true },
    modelValue: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue']);

const { rooms, loading, error, fetchRooms } = useSpaBreakRooms(props.apiUrl);

const selectedRoomCode = ref(null);
const selectedConfiguration = reactive({});

// Radio group names have to be unique across the whole page, not just this picker.
const uid = useId();

watch(
    () => [props.spaBreakId, props.locationId, props.date],
    () => {
        selectedRoomCode.value = null;
        fetchRooms(props.spaBreakId, props.locationId, props.date);
    },
    { immediate: true }
);

const selectedRoom = computed(() => rooms.value.find(r => r.code === selectedRoomCode.value) ?? null);

// Emits the completed selection - null while incomplete (no room chosen, or a room with
// configuration options where none has been picked yet). CalendarBookingPanel uses this to
// decide whether a "Continue" link should appear at all.
watch([selectedRoomCode, selectedConfiguration], () => {
    if (!selectedRoom.value) {
        emit('update:modelValue', null);
        return;
    }

    const configuration = selectedConfiguration[selectedRoom.value.code] ?? null;

    if (selectedRoom.value.configurations.length && !configuration) {
        emit('update:modelValue', null);
        return;
    }

    emit('update:modelValue', {
        code:          selectedRoom.value.code,
        rateCode:      selectedRoom.value.rateCode,
        configuration,
    });
}, { deep: true });

function extraChargeLabel(room) {
    if (room.extraCharge === null) return null;
    return room.extraCharge > 0 ? `+£${(room.extraCharge / 100).toFixed(2)}` : 'Included';
}
</script>

<template>
    <RadioGroup legend="Room" :class="{ radio_group_loading: loading }">
        <template v-if="loading">
            <div v-for="n in 3" :key="n" class="radio_option_placeholder"></div>
        </template>

        <p v-else-if="error" class="form_error">{{ error }}</p>

        <p v-else-if="!rooms.length">No rooms available for this date.</p>

        <template v-else>
            <RadioRich
                v-for="room in rooms"
                :key="room.code"
                v-model="selectedRoomCode"
                :name="`${uid}-room`"
                :value="room.code"
                :label="room.name"
                :detail="extraChargeLabel(room)"
                required
            >
                <p v-if="room.description">{{ room.description }}</p>

                <!-- No @click.stop: the select sits outside the header label, and
                     radio-options.scss lifts interactive content above the label's
                     stretched hit area. See RadioRich.vue. -->
                <div v-if="room.configurations.length" class="field">
                    <label :for="`${uid}-${room.code}-configuration`">Bed configuration</label>
                    <select
                        :id="`${uid}-${room.code}-configuration`"
                        v-model="selectedConfiguration[room.code]"
                        required
                    >
                        <option v-for="config in room.configurations" :key="config" :value="config">
                            {{ config }}
                        </option>
                    </select>
                </div>
            </RadioRich>
        </template>
    </RadioGroup>
</template>
